import { inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { isEmpty } from 'lodash';
import { lastValueFrom } from 'rxjs';
import { RollbarService } from '../../../cross-team-shared/messaging/rollbar.service';
import { AnnotationEntityExternal } from '../../../team-apis/otterdam/annotations';
import { Submission } from '../../../team-apis/otterdam/submission';
import { Task } from '../../../team-apis/otterdam/task';
import { TaskWorkspaceState } from '../../h3ml/state/task-workspace-state';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { TimerService } from '../../production-workspace/timer-badge/timer.service';
import { FeatureFlagService } from '../../shared/misc-services/feature-flag.service';
import { TrainingTaskService } from '../../shared/misc-services/training-task.service';
import {
  annotationPath,
  AnnotationResource,
} from '../../shared/resources/annotation-resource.service';
import {
  isObjectEntity,
  isObjectRelatedEntity,
  isShapeRelatedEntity,
} from '../../shared/utils/annotation-external-utils';
import { AbstractResource } from '../abstract-resource';
import { filterOrphanEntities } from './filter-orphan-entities';
import {
  buildObjectsWithoutLocation,
  initializeAnnotations,
  initializeObjects,
  initializeShapes,
  initializeTrainingShapes,
  resetPreviewAnnotations,
} from './shape-initializer-utils';
import { PRE_ANNOTATIONS_SUBMISSION_ID } from './submission-loading.service';

export const inputObjectClassNames = new Set([
  'image',
  'video',
  'pointcloud',
  'text',
  'audio',
  'scene',
  'dialog',
  'prompt',
  'response',
]);

@Injectable()
export class AnnotationLoadingService extends AbstractResource {
  private annotationResourceService = inject(AnnotationResource);
  private featureFlagService = inject(FeatureFlagService);
  private rollbarService = inject(RollbarService);
  private store = inject(TaskWorkspaceStore);
  private timerService = inject(TimerService);
  private trainingTaskService = inject(TrainingTaskService);

  async load(task: Task, matchedSubmission?: Submission) {
    if (!this.store.isUsingObjects()) {
      return;
    }

    const path = this.getPath(task, matchedSubmission);
    let annotations = await this.getAnnotations(path);

    if (!this.store.isUsingGenAi() && !this.store.isUsingCategorization()) {
      // temporary until orphan entities are no longer produced by workspace
      await this.filterOrphanEntities(annotations);
      annotations = this.filterOutInputObjects(annotations);
    }

    try {
      this.store.batch(() => {
        initializeAnnotations(this.store, this.timerService, annotations);
      });
    } catch (error: any) {
      console.error(error);
      this.rollbarService.error('Failed to initialize annotations', error);
      this.messagingService.error('Failed to initialize annotations.');
    }
  }

  async loadPreviousAnnotations(submission?: Submission) {
    assert(
      this.store.isUsingGenAi(),
      'this method should only be called for gen ai projects',
    );

    const { task } = this.store.state();
    const { projectId } = this.workspaceConfig;
    const path = annotationPath({
      projectId,
      taskId: task.id,
      round: submission?.round ?? 0,
      stepId: submission?.step_id,
    });
    const annotations = await this.getAnnotations(path);
    const objects = buildObjectsWithoutLocation(
      annotations.filter(isObjectRelatedEntity),
    );

    const submissionStore = this.store('submissions')(
      submission?.id ?? PRE_ANNOTATIONS_SUBMISSION_ID,
    );
    this.store.batch(() => {
      initializeSubmissionStore(submissionStore);
      submissionStore('objects').set(objects);
    });
  }

  async loadShapesForTraining(task: Task) {
    if (
      !this.store.isUsingObjects() ||
      !this.trainingTaskService.isInPracticeMode()
    ) {
      return;
    }

    const annotations = await lastValueFrom(
      this.annotationResourceService.getTrainingAnnotations$(task),
    );

    initializeTrainingShapes(
      this.store,
      annotations.filter(isShapeRelatedEntity),
    );
  }

  async loadAnnotationsForTraining() {
    const task = this.store.state().task;
    const annotations = await lastValueFrom(
      this.annotationResourceService.getTrainingAnnotations$(task),
    );

    resetPreviewAnnotations(this.store);
    this.store.batch(() => {
      initializeShapes(this.store, annotations.filter(isShapeRelatedEntity));
      initializeObjects(this.store, annotations.filter(isObjectRelatedEntity));
    });
  }

  async loadPreAnnotations(taskId: string) {
    const { projectId } = this.workspaceConfig;
    return this.getAnnotations(
      annotationPath({
        projectId,
        taskId,
        round: 0,
        stepId: undefined,
      }),
    );
  }

  async loadSubmissionAnswers(task: Task, submission?: Submission) {
    const { projectId } = this.workspaceConfig;
    const annotations = await this.getAnnotations(
      annotationPath({
        projectId,
        taskId: task.id,
        round: submission?.round ?? 0,
        stepId: submission?.step_id,
      }),
    );

    resetPreviewAnnotations(this.store);
    this.store.batch(() => {
      initializeShapes(this.store, annotations.filter(isShapeRelatedEntity));
      initializeObjects(this.store, annotations.filter(isObjectRelatedEntity));
    });
  }

  private async filterOrphanEntities(annotations: AnnotationEntityExternal[]) {
    const orphans = filterOrphanEntities(annotations);

    if (
      this.workspaceConfig.name === 'production' &&
      this.featureFlagService.hasFlag('delete_ghost_shapes') &&
      !isEmpty(orphans)
    ) {
      this.rollbarService.error('Orphan entities found', orphans);
      await lastValueFrom(
        this.annotationResourceService.deleteEntities$(orphans),
      );
    }
  }

  private filterOutInputObjects(annotations: AnnotationEntityExternal[]) {
    return annotations.filter(
      (annotation) =>
        !isObjectEntity(annotation) ||
        !annotation.className ||
        !inputObjectClassNames.has(annotation.className),
    );
  }

  private getAnnotations(path: string) {
    return lastValueFrom(
      this.request$<AnnotationEntityExternal[]>('GET', path, {
        customErrorMessage: 'Failed to load annotations.',
        tokenScope: 'access_token',
      }),
    );
  }

  private getPath(task: Task, matchedSubmission?: Submission) {
    const { id: taskId } = task;
    const { name, projectId, stepId: currentStepId } = this.workspaceConfig;
    let round = task.round;
    let stepId = currentStepId;

    if (name === 'sampling') {
      round = this.store.getLastSubmission().round;
      stepId = this.store.getLastSubmission().step_id;
    } else if (name === 'readOnly') {
      const { round: newRound, stepId: newStepId } =
        this.buildReadOnlyWorkspaceStepAndRound(task, matchedSubmission);
      round = newRound;
      stepId = newStepId;
    }

    return annotationPath({ projectId, taskId, round, stepId });
  }

  private buildReadOnlyWorkspaceStepAndRound(
    task: Task,
    matchedSubmission?: Submission,
  ) {
    const { submissions } = task;
    const { stepId: currentStepId } = this.workspaceConfig;
    let round = task.round;
    let stepId = currentStepId;

    if (!submissions?.length && task.state === 'new') {
      round = 0;
      stepId = undefined;
    } else if (matchedSubmission) {
      round = matchedSubmission.round;
      stepId = matchedSubmission.step_id;
    }

    return { round, stepId };
  }
}

function initializeSubmissionStore(
  store: Store<TaskWorkspaceState['submissions'][number]>,
): asserts store is Store<
  NonNullable<TaskWorkspaceState['submissions'][number]>
> {
  store.setUsing(
    (submissionState) => submissionState ?? { objects: {}, shapeFeedback: [] },
  );
}
